import axios from "axios";
import { mapThemeSettings } from "./mapThemeSettings"; 
import { OVIZIA_SERVICE_BASE_URL } from "constants"; 
import { fetchDomainMappings, fetchWebsiteDomains } from "./domainService";
import apiClient from "utils/apiClient";

const API_BASE = `${OVIZIA_SERVICE_BASE_URL}/api/v1/websites`;
const BASE_URL = OVIZIA_SERVICE_BASE_URL;

export async function getWebsite(websiteId) {
  try {
    const response = await apiClient.get(`${API_BASE}/${websiteId}`);

    // console.log(response.data);
    return response.data;
  } catch (error) {
    console.log(error);
  }
}

export async function getContent(websiteId) {
  try {
    const response = await apiClient.get(
      `${API_BASE}/${websiteId}/content/active`,
    );

    // console.log(response.data);
    return response.data;
  } catch (error) {
    console.log(error);
  }
}

export function normalizeWebsiteContent(apiResponse) {
  const raw = apiResponse?.contentJson;

  const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

  return {
    id: apiResponse.id,
    websiteId: apiResponse.websiteId,

    // full structured content
    content: parsed,

    // extracted shortcuts
    theme: mapThemeSettings(parsed?.themeSettings),
    sections: parsed?.sections || [],
    seo: parsed?.seo || {},
  };
}

export async function getWebsiteForEditor(websiteId) {
  const raw = await getContent(websiteId);
  console.log(normalizeWebsiteContent(raw));
  return normalizeWebsiteContent(raw);
}

export async function publishWebsite(websiteId, deploymentId) {
  if (!deploymentId) {
    throw new Error("A draft deployment is required before publishing.");
  }

  try {
    const response = await apiClient.post(
      `${API_BASE}/${websiteId}/publish?deploymentId=${deploymentId}`,
    );
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function regenerateWebsite(websiteId, template) {
  try {
    const response = await apiClient.post(
      `${API_BASE}/${websiteId}/generate?template=${template}`,
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
}


export async function regenerateWebsiteContent(websiteId) {
  const { data } = await apiClient.post(
    `${API_BASE}/${websiteId}/regenerate`,
  );
  return data;
}

const LIVE_SITE_BASE_URL =
  process.env.REACT_APP_ENV == "dev"
    ? "https://dev.sites.ovizia.com"
    : "https://sites.ovizia.com";

export function getWebsitePreviewUrl(slug) {
  if (!slug) return null;
  return `${LIVE_SITE_BASE_URL}/${slug}`;
}

export async function resolveWebsiteUrl(site, workspaceId) {
  const isDev = process.env.REACT_APP_ENV === "dev";

  try {
    const mappings = await fetchDomainMappings(workspaceId);

    const mapping = Array.isArray(mappings)
      ? mappings.find((m) => m.websiteId === site.id && m.primaryDomain)
        ?? mappings.find((m) => m.websiteId === site.id)
      : null;

    if (mapping) {
      // in dev: DNS is dummy/unresolvable, but the LB serves the site fine
      // directly off the IP, so skip domain resolution entirely.
      if (isDev && mapping.targetIpAddress) {
        return `http://${mapping.targetIpAddress}/`;
      }

      // prod: domain should resolve properly through DNS -> LB.
      if (!isDev && mapping.status === "ACTIVE" && mapping.domainName) {
        return `https://${mapping.domainName}`;
      }
    }
  } catch {
    // mapping lookup failed — fall back to free subdomain below
  }

  return getWebsitePreviewUrl(site.slug);
}

export const addSectionItem = async (websiteId, sectionType, defaultItem) => {
  const { data } = await apiClient.post(
    `/api/v1/websites/${websiteId}/content/mutate/batch`,
    { mutations: buildAddItemMutations(sectionType, defaultItem) },
    { baseURL: BASE_URL },
  );
  return data;
};

// builds one mutation per field in the default item,
// all targeting the same new index
function buildAddItemMutations(sectionType, defaultItem, newIndex) {
  return Object.entries(defaultItem).map(([field, value]) => ({
    sectionType,
    itemIndex: newIndex,
    field,
    value,
    mutationType: "FIELD",
  }));
}


export async function revertContentVersion(websiteId, versionId) {
  const { data } = await apiClient.post(
    `/api/v1/websites/${websiteId}/content/versions/${versionId}/revert`,
    {},
    { baseURL: BASE_URL }
  );
  return data;
}


export async function archiveWebsite(websiteId) {
  const { data } = await apiClient.post(`${API_BASE}/${websiteId}/archive`);
  return data;
}